import React, { useLayoutEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ButtonGhostV2,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_CAPTION_SEMIBOLD_DF,
  icons,
} from "oolib";
import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import { t } from "../theme";

const { Tag, ArrowLineUpRight } = icons;

const Wrap = styled.div<{ $showPointer: boolean }>`
  position: relative;
  cursor: ${({ $showPointer }) => ($showPointer ? "pointer" : "default")};
`;

const CaretAbs = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
`;

const HeightWrap = styled(motion.div)`
  position: relative;
  overflow: hidden;
`;

const Body = styled(motion.div)`
  padding-right: 2.5rem;
  min-height: 2.4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Layer = styled(motion.div)`
  /* Both layers stack at the same top-left; only the active one occupies
     the wrapper's height (via measured height on HeightWrap). */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Title = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${t("grey80")};
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.3rem;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const FieldLabel = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${t("grey80")};
`;

const Pill = styled.span`
  background: ${t("grey5")};
  border-radius: 999px;
  padding: 0.4rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

// Same visual as `Pill`, but a router link. Hover bumps the background so
// the affordance is obvious even before the cursor reveals it's clickable.
const PillLink = styled(NavLink)`
  background: ${t("grey5")};
  border-radius: 999px;
  padding: 0.4rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  text-decoration: none;
  transition: background 0.15s ease;
  &:hover {
    background: ${t("grey10")};
  }
`;

const PillLabel = styled(UI_CAPTION_SEMIBOLD_DF)`
  color: ${t("grey80")};
`;

export type DocumentTagsField = {
  iconName?: string;
  iconWeight?: "regular" | "bold" | "fill" | "light";
  label: string;
  value: string;
  /** When set, the value pill renders as a router link with a trailing
   *  ArrowLineUpRight — used for content-tag fields whose value points at
   *  another document. Built upstream (page-level) via
   *  getRoutePathToPublishedContent + optional ?activeTab=. */
  link?: string;
};

export type DocumentTagsCardProps = {
  fields?: DocumentTagsField[];
};

type Field = DocumentTagsField;

const FieldRowContent: React.FC<{ field: Field }> = ({ field }) => (
  <FieldRow>
    {field.iconName &&
      (genIcon as any)({
        icon: field.iconName,
        weight: field.iconWeight || "regular",
        color: t("grey80"),
        size: 18,
      })}
    <FieldLabel>{field.label}</FieldLabel>
    {field.link ? (
      <PillLink to={field.link} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <PillLabel>{field.value}</PillLabel>
        <ArrowLineUpRight size={14} color={t("grey80")} weight="bold" />
      </PillLink>
    ) : (
      <Pill>
        <PillLabel>{field.value}</PillLabel>
      </Pill>
    )}
  </FieldRow>
);

const TWEEN = { duration: 0.27, ease: [0.32, 0.72, 0, 1] as const };
const FADE = { duration: 0.18 };

export const DocumentTagsCard: React.FC<DocumentTagsCardProps> = ({ fields = [] }) => {
  // Disable the collapse affordance when there's nothing meaningful to hide
  // (one row or fewer). The caret is suppressed, the wrap loses its pointer
  // cursor, and the click-to-toggle handler is detached — so a stray click
  // can't flip into an awkward "collapsed but identical-looking" state.
  const collapsible = fields.length > 1;
  const [expanded, setExpanded] = useState(true);
  const toggle = () => {
    if (!collapsible) return;
    setExpanded((p) => !p);
  };

  const collapsedRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useLayoutEffect(() => {
    const measure = () => {
      const target = expanded ? expandedRef.current : collapsedRef.current;
      if (target) setHeight(target.scrollHeight);
    };
    measure();
    const target = expanded ? expandedRef.current : collapsedRef.current;
    if (!target) return;
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    return () => ro.disconnect();
  }, [expanded, fields]);

  return (
    <Wrap onClick={toggle} $showPointer={collapsible && !expanded}>
      {collapsible && (
        <CaretAbs>
          <ButtonGhostV2
            icon={expanded ? "CaretUp" : "CaretDown"}
            iconColor={t("grey80")}
            style={{ padding: 4 }}
          />
        </CaretAbs>
      )}
      <HeightWrap animate={{ height }} initial={false} transition={TWEEN}>
        <Layer
          ref={collapsedRef}
          initial={false}
          animate={{ opacity: expanded ? 0 : 1 }}
          transition={FADE}
          style={{ pointerEvents: expanded ? "none" : "auto" }}
        >
          <Body>
            <TitleRow>
              <Tag size={18} color={t("grey80")} />
              <Title>Document Tags</Title>
            </TitleRow>
          </Body>
        </Layer>
        <Layer
          ref={expandedRef}
          initial={false}
          animate={{ opacity: expanded ? 1 : 0 }}
          transition={FADE}
          style={{ pointerEvents: expanded ? "auto" : "none" }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Body>
            <FieldsList>
              {fields.map((f) => (
                <FieldRowContent key={f.label} field={f} />
              ))}
            </FieldsList>
          </Body>
        </Layer>
      </HeightWrap>
    </Wrap>
  );
};

export default DocumentTagsCard;
